// © Microsoft Corporation. All rights reserved.

import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Label, Spinner, SpinnerSize } from '@fluentui/react';
import { RemoteVideoStream, VideoStreamRenderer, VideoStreamRendererView } from '@azure/communication-calling';
import { videoHint, mediaContainer, labelnamevideo } from './styles/StreamMedia.styles';
import { utils } from 'Utils/Utils';
import staticMediaSVG from '../assets/staticmedia.svg';
import { Image, ImageFit } from '@fluentui/react';

export interface RemoteStreamMediaProps {
  label: string;
  stream: RemoteVideoStream | undefined;
  isParticipantStreamSelected: boolean;
  selectedPane:string;
}

export const RemoteStreamMedia = (props: RemoteStreamMediaProps): JSX.Element => {
  const rendererView = useRef<VideoStreamRendererView | undefined>();

  const streamId = props.stream ? utils.getStreamId(props.label, props.stream) : `${props.label} - no stream`;

  const [activeStreamBeingRendered, setActiveStreamBeingRendered] = useState(false);
  const [showRenderLoading, setShowRenderLoading] = useState(false);


  const imageProps = {
    src: staticMediaSVG.toString(),
    imageFit: ImageFit.contain,
    styles: {
      root: {
        width: '100%',
        height: '100%',
        display: activeStreamBeingRendered ? 'none' : 'block'
      }
    }
  };

  const loadingStyle = {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const { label, stream, isParticipantStreamSelected, selectedPane } = props;

  const renderRemoteStream = useCallback(async () => {
    const container = document.getElementById(streamId);
    if (container && stream && stream.isAvailable && isParticipantStreamSelected) {
      // if we are already rendering a stream we don't want to start rendering the same stream
      if (activeStreamBeingRendered) {
        return;
      }

      // set the flag that a stream is being rendered
      setActiveStreamBeingRendered(true);
      setShowRenderLoading(true);
      const renderer: VideoStreamRenderer = new VideoStreamRenderer(stream);
      // this can block a really long time if we fail to be subscribed to the call and it has to retry
      rendererView.current = await renderer.createView({ scalingMode: 'Crop' });
      setShowRenderLoading(false);
      if (container && container.childElementCount === 0) {
        container.appendChild(rendererView.current.target);
      }
    } else {
      setActiveStreamBeingRendered(false);

      if (rendererView.current) {
        rendererView.current.dispose();
        rendererView.current = undefined;
      }
    }
  }, [stream, isParticipantStreamSelected, setShowRenderLoading, setActiveStreamBeingRendered, activeStreamBeingRendered, rendererView, streamId]);

  useEffect(() => {
    if (!stream) {
      return;
    }

    stream.on('isAvailableChanged', renderRemoteStream);

    if (stream.isAvailable) {
      renderRemoteStream();
    }
  }, [stream, isParticipantStreamSelected, renderRemoteStream]);

  return (
    <div className={mediaContainer} style={{ background: '#1f1e1e' }}>
     <div className={selectedPane != 'none' ? ' mob_video_bg_panelm1' : ' full_mob_video_bg_panelm1'}>
        <div style={{ display: activeStreamBeingRendered ? 'block' : 'none', }} className={mediaContainer} id={streamId}>
          {showRenderLoading && <Spinner style={loadingStyle} label={`Rendering stream...`} size={SpinnerSize.xSmall} />}
        </div>
        <div>
          <Image className={selectedPane != 'none' ? ' mob_video_image_view' : ' full_mob_video_image_view'} style={{ position: 'relative', left: '0px', right: '0px', top: '0px', transform: 'translate(0%, 0%)' }} {...imageProps} />
        </div>
        <Label className={[videoHint, labelnamevideo].join(' ') +' mob_video_name_text'}>{label}</Label>
      </div >
    </div>
  );
};
